import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { getRefundOptionsQueryFn } from '../graphql/query/getRefundOptions';
import { getInput } from '../components/tasks/helpers';
import { useDeliveryOptions } from './useDeliveryOptions';
import { useReasons } from './useReasons';
import { useItems } from './useItems';
import { QUERIES } from '../utils/constants';
import { generateQueryKey } from '../utils/queries';
import { useWorkflow } from '../workflow/hooks';
import type { RefundContentData } from '../components/tasks/RefundTask/RefundTaskSchema';
import { useResolutions } from './useResolutions';
import { addShipmentScopeItemsToSubmitData } from '../utils/shipmentScopeUtils';
import { useErrorHandler } from '../utils/handleErrorCodes';

export const useRefundOptions = () => {
  const {
    config,
    data: workflowData,
    order,
    requestId,
    setRefundAmount,
  } = useWorkflow();
  const { watch } = useFormContext();
  const handleErrorCodes = useErrorHandler();

  const { refund } = workflowData ?? {};
  const currentRefundMethod = refund as RefundContentData;

  // Watch the form value - this is the source of truth for the selected option
  const selectedRefundOption =
    watch('refund.method') || currentRefundMethod?.method;

  const items = useItems();

  const resolutionsByItem = useResolutions();

  const reasonsByItem = useReasons();

  const {
    isLoading: isLoadingDeliveryOptions,
    selectedMethod,
    selectedMethodKey,
  } = useDeliveryOptions();

  // Include shipment scope items in the refund calculation
  const updatedFormData = addShipmentScopeItemsToSubmitData(
    workflowData,
    order,
  );

  const variables = getInput({
    requestId,
    customer: order?.customer,
    deliveryMethod: selectedMethod,
    items: order?.items, // Use all order items, not just cart items
    formItems: updatedFormData?.items || workflowData?.items,
    orderTokens: order?.orderTokens,
    orderId: order?.orderId,
    reasons: reasonsByItem,
    refund: selectedRefundOption
      ? { method: selectedRefundOption }
      : currentRefundMethod,
    resolutionsByItem,
  });

  const isLoadingDependencies = isLoadingDeliveryOptions;

  const queryKey = generateQueryKey(QUERIES.GET_REFUND_OPTIONS, {
    taskIds: ['reason', 'resolution', 'returnMethod'],
    workflowConfig: config,
    workflowData,
  });

  const { data, isLoading } = useQuery({
    retry: false,
    enabled: Boolean(
      !isLoadingDependencies && items.length > 0 && selectedMethod,
    ),
    queryKey: [...queryKey, selectedMethodKey, selectedRefundOption],
    queryFn: async () => {
      try {
        return await getRefundOptionsQueryFn(variables);
      } catch (error: unknown) {
        handleErrorCodes(error);
        throw error;
      }
    },
  });

  // Sync the selected option's total estimate into workflow context for global access
  useEffect(() => {
    if (!data?.refundOptions || !selectedRefundOption) {
      setRefundAmount(null);
      return;
    }

    const selected = data.refundOptions.find(
      (option) => option.type === selectedRefundOption,
    );

    if (!selected) {
      setRefundAmount(null);
      return;
    }

    const breakdown = selected.estimateBreakdown || [];
    const refundAmount = breakdown[breakdown.length - 1]?.monetaryValue;

    if (refundAmount) {
      setRefundAmount(refundAmount);
    } else {
      setRefundAmount(null);
    }
  }, [data?.refundOptions, selectedRefundOption, setRefundAmount]);

  return {
    isLoading: isLoading || isLoadingDependencies,
    selectedRefundOption,
    refundOptions: data?.refundOptions || [],
  };
};
